/* eslint-disable @typescript-eslint/no-unused-vars*/
import React, {useState} from 'react';
import {Grid, IconButton, Typography} from '@mui/material';
import SearchBarController from 'controllers/SearchBarController/SearchBarController';
import Button from 'components/button/Button';
import Chip from 'components/Chip/Chip';
import GqlTableController from 'controllers/GqlTableController/GqlTableController';
import {ColumnProps} from 'components/StickyHeadTable/StickyHeadTable';
import {GET_ADDITIONAL_LEAVES, FILTER_USERS} from 'graphql/absences/Querys';
import ModalController from 'controllers/ModalController';
import AdditionalLeaveCreateModal, {
  type SelectionMode,
} from 'viewComponents/AdditiionalLeaveCreateModal/AdditionalLeaveCreateModal';
import DeleteIcon from '@mui/icons-material/Delete';
import AdditionalLeaveDeleteModal from 'viewComponents/AdditionalLeaveDeleteModal/AdditionalLeaveDeleteModal';
import {useTranslation} from 'react-i18next';
import {formatDateByLocale} from 'commons/formatDate';

interface UserAutocompleteItem {
  id: string;
  firstName: string;
  lastName: string;
}

const AdditionalLeaves = () => {
  const {t, i18n} = useTranslation();
  const [username, setUsername] = useState('');
  const [selectedNameLabel, setSelectedNameLabel] = useState('');
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('PERSON');

  const columns: ColumnProps[] = [
    {
      id: 'name',
      label: t('additionalLeaves.name'),
    },
    {
      id: 'expirationDate',
      label: t('additionalLeaves.expirationDate'),
      render: (value: string) =>
        formatDateByLocale(value, i18n.language as 'en' | 'es' | 'fr' | 'pt'),
    },
    {
      id: 'days',
      label: t('additionalLeaves.days'),
    },
    {
      id: 'activationDate',
      label: t('additionalLeaves.activationDate'),
      render: (value: string) =>
        formatDateByLocale(value, i18n.language as 'en' | 'es' | 'fr' | 'pt'),
    },
    {
      id: 'type',
      label: t('additionalLeaves.reason'),
    },
    {
      id: 'description',
      label: t('additionalLeaves.comments'),
    },
    {
      id: 'id',
      label: '',
      render: (id: string, {userId}) => (
        <ModalController
          trigger={({onToggleModal}) => (
            <IconButton onClick={onToggleModal}>
              <DeleteIcon />
            </IconButton>
          )}
          modal={({isModalOpen, onToggleModal}) => (
            <AdditionalLeaveDeleteModal
              additionalLeaveId={id}
              userId={userId}
              open={isModalOpen}
              onClose={onToggleModal}
            />
          )}
        />
      ),
    },
  ];

  const renderNameInput = () => {
    return (
      <div className="flex flex-col">
        <SearchBarController<UserAutocompleteItem>
          query={FILTER_USERS}
          queryVariableName="keyword"
          queryDataPath={['filterUsers']}
          placeholder={t('AbsenceSummary.search')}
          transformResult={user => ({
            id: user.id,
            text: `${user.firstName} ${user.lastName}`,
            selected: false,
          })}
          onResultSelect={user => {
            const fullName = `${user.firstName} ${user.lastName}`;
            setUsername(fullName);
            setSelectedNameLabel(fullName);
          }}
        />
        {selectedNameLabel && (
          <div className="mt-2">
            <Chip
              id="selected-user"
              text={selectedNameLabel}
              size="small"
              color="primary"
              onRemove={() => {
                setUsername('');
                setSelectedNameLabel('');
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full flex flex-col flex-wrap pt-12">
      <Typography variant="h5">{t('additionalLeaves.title')}</Typography>
      <Grid justifyContent="center" direction="column" container>
        <Grid
          className="mt-8"
          container
          direction="row"
          justifyContent="space-between"
        >
          {renderNameInput()}
          <ModalController
            trigger={({onToggleModal}) => (
              <Button
                text={t('additionalLeaves.assignButton')}
                onClick={onToggleModal}
              />
            )}
            modal={({isModalOpen, onToggleModal}) => (
              <AdditionalLeaveCreateModal
                onClose={() => {
                  setSelectionMode('PERSON');
                  onToggleModal();
                }}
                open={isModalOpen}
                selectionMode={selectionMode}
                onSelectionModeChange={setSelectionMode}
              />
            )}
          />
        </Grid>
        <Grid className="mt-4">
          <GqlTableController
            columns={columns}
            placeholder={t('additionalLeaves.noData')}
            updateTrigger={username}
            queryConfiguration={{
              query: GET_ADDITIONAL_LEAVES,
              rootField: 'additionalLeaves',
              queryVariables: (page: number, pageSize: number) => {
                return {
                  sort: {
                    field: 'activationDate',
                    order: 'desc',
                  },
                  username,
                  pageParams: {
                    page,
                    size: pageSize,
                  },
                };
              },
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default AdditionalLeaves;
